import React, { useMemo, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Timeline } from 'primereact/timeline';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';

// API & Services
import {
    useGetApprovalDetailQuery,
    useLazyGetInvoiceDocumentByIdQuery
} from '../../../../../api/endpoints/dealerInvoiceEntryApi';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../utils/documentDownloadService';
import { InvoiceStatus } from '../constants/invoiceStatus';

const DealerInvoiceHistoryDialog = ({ visible, onHide, record, th }) => {
    const toast = useRef(null);

    // 1. Tablo satırından ID'yi al
    const recordId = record?.id || record?.recordId;

    // 2. Detay Verisini Çek
    const { data: detailResponse, isFetching: isLoadingDetail } = useGetApprovalDetailQuery(
        recordId,
        {
            skip: !visible || !recordId,
            refetchOnMountOrArgChange: true
        }
    );

    // Detay verisini parse et
    const activeRecord = useMemo(() => {
        if (detailResponse?.data?.items?.length > 0) {
            return detailResponse.data.items[0];
        }
        return detailResponse?.data || record;
    }, [detailResponse, record]);

    // Group ID'yi al
    const targetGroupId = activeRecord?.groupId || activeRecord?.vbeln;

    // DÜZELTME: Para birimini record'dan veya activeRecord'dan al
    const currency = activeRecord?.currency || record?.currency || 'TRY';

    // --- A) LİSTE ÇEKME İŞLEMİ ---
    const [triggerGetList, { data: listData, isFetching: isLoadingList }] = useLazyGetInvoiceDocumentByIdQuery();

    useEffect(() => {
        if (visible && targetGroupId) {
            triggerGetList(targetGroupId);
        }
    }, [visible, targetGroupId, triggerGetList]);

    const documentList = useMemo(() => {
        const rawData = listData?.data || listData;
        if (Array.isArray(rawData)) return rawData;
        if (rawData?.items && Array.isArray(rawData.items)) return rawData.items;
        return [];
    }, [listData]);

    // --- B) İNDİRME İŞLEMİ ---
    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();
    const downloadService = useMemo(() => new DocumentDownloadService(toast), []);

    const handleDownloadDocument = async (doc) => {
        const key = doc.fileKey || doc.id;
        if (!key) {
            console.error("Doküman anahtarı bulunamadı");
            return;
        }

        toast.current?.show({
            severity: 'info',
            summary: th('common.downloading'),
            detail: th('common.downloading'),
            life: 3000
        });

        try {
            await downloadService.downloadByKey(
                triggerDownloadByKey,
                key,
                null,
                {
                    successMessage: th('common.success'),
                    errorMessage: th('common.error')
                }
            );
        } catch (error) {
            console.error("Download Error", error);
        }
    };

    // --- Timeline Olayları ---
    const events = useMemo(() => {
        if (!activeRecord) return [];
        const timeline = [];

        if (activeRecord.status === InvoiceStatus.DRAFT) {
            timeline.push({
                status: th('dealerInvoice.history.draftCreated'),
                date: activeRecord.createdDate,
                icon: 'pi pi-file-edit',
                color: '#607D8B'
            });
        }
        // Gönderildi
        if (activeRecord.submittedDate || (activeRecord.status !== InvoiceStatus.DRAFT && activeRecord.invoiceDate)) {
            timeline.push({
                status: th('dealerInvoice.history.submitted'),
                date: activeRecord.submittedDate || activeRecord.invoiceDate,
                icon: 'pi pi-send',
                color: '#2196F3',
                user: activeRecord.submittedBy || 'Bayi'
            });
        }
        // İade
        if (activeRecord.status === InvoiceStatus.RETURNED || activeRecord.returnedDate) {
            timeline.push({
                status: th('dealerInvoice.history.returned'),
                date: activeRecord.returnedDate,
                icon: 'pi pi-replay',
                color: '#FF9800',
                comment: activeRecord.returnReason,
                user: activeRecord.returnedBy
            });
        }
        // Red
        if (activeRecord.status === InvoiceStatus.REJECTED || activeRecord.rejectedDate) {
            timeline.push({
                status: th('dealerInvoice.history.rejected'),
                date: activeRecord.rejectedDate,
                icon: 'pi pi-times',
                color: '#F44336',
                comment: activeRecord.rejectionReason,
                user: activeRecord.rejectedBy
            });
        }
        // Onay
        if (activeRecord.status === InvoiceStatus.APPROVED || activeRecord.approvedDate) {
            timeline.push({
                status: th('dealerInvoice.history.approved'),
                date: activeRecord.approvedDate,
                icon: 'pi pi-check',
                color: '#4CAF50',
                user: activeRecord.approvedBy
            });
        }
        // SAP
        if (activeRecord.localApprovalStatus === 'PROCESSED_TO_SAP') {
            timeline.push({
                status: th('dealerInvoice.history.sentToSAP'),
                date: activeRecord.processedDate || activeRecord.approvedDate,
                icon: 'pi pi-cloud-upload',
                color: '#9C27B0'
            });
        }

        return timeline;
    }, [activeRecord, th]);

    const customizedMarker = (item) => (
        <span className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-1" style={{ backgroundColor: item.color }}>
            <i className={item.icon}></i>
        </span>
    );

    const customizedContent = (item) => (
        <Card
            title={item.status}
            subTitle={item.date ? new Date(item.date).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
            className="mb-3 shadow-1"
        >
            {item.user && <div className="text-sm text-gray-600 mb-2"><i className="pi pi-user mr-2"></i>{item.user}</div>}
            {item.comment && (
                <div className="surface-100 p-2 border-round text-sm">
                    <strong>{th('common.note')}:</strong> {item.comment}
                </div>
            )}
        </Card>
    );

    return (
        <Dialog
            header={th('dealerInvoice.history.title')}
            visible={visible}
            style={{ width: '60vw', minWidth: '800px' }}
            onHide={onHide}
            modal
            maximizable
        >
            <Toast ref={toast} />

            {isLoadingDetail && !activeRecord ? (
                <div className="flex justify-content-center p-5"><ProgressSpinner /></div>
            ) : activeRecord ? (
                <div className="grid">
                    {/* SOL TARAF: Bilgiler ve Dosyalar */}
                    <div className="col-12 md:col-5">

                        {/* Fatura Bilgileri */}
                        <div className="surface-card p-3 shadow-2 border-round mb-3">
                            <div className="text-xl font-bold mb-3 text-primary border-bottom-1 surface-border pb-2">
                                {th('dealerInvoice.newEntryInfo')}
                            </div>
                            <div className="field mb-2">
                                <span className="font-semibold block">{th('dealerInvoice.groupId')}</span>
                                <span>{activeRecord.groupId || activeRecord.vbeln}</span>
                            </div>
                            <div className="field mb-2">
                                <span className="font-semibold block">{th('dealerInvoice.invoiceNumber')}</span>
                                <span>{activeRecord.invoiceNumber || '-'}</span>
                            </div>
                            <div className="field mb-2">
                                <span className="font-semibold block">{th('dealerInvoice.totalPrice')}</span>
                                {/* DÜZELTME: Sabit TRY yerine dinamik currency kullanılıyor */}
                                <span className="text-green-600 font-bold">
                                    {new Intl.NumberFormat('tr-TR', {
                                        style: 'currency',
                                        currency: currency
                                    }).format(activeRecord.totalAmount || activeRecord.totalPrice || 0)}
                                </span>
                            </div>
                        </div>

                        {/* Dokümanlar */}
                        <div className="surface-card p-3 shadow-2 border-round">
                            <div className="flex align-items-center justify-content-between mb-3 border-bottom-1 surface-border pb-2">
                                <span className="text-xl font-bold text-primary">
                                    <i className="pi pi-file mr-2"></i>{th('dealerInvoice.documents')}
                                </span>
                                {(documentList.length > 0) && <Tag value={documentList.length} severity="info" />}
                            </div>

                            {isLoadingList ? (
                                <div className="flex justify-content-center p-2"><ProgressSpinner style={{ width: '30px', height: '30px' }} /></div>
                            ) : documentList.length > 0 ? (
                                <div className="flex flex-column gap-2">
                                    {documentList.map((doc, index) => (
                                        <div key={index} className="flex align-items-center justify-content-between p-2 surface-50 border-round hover:surface-100 transition-colors cursor-pointer border-1 surface-border">
                                            <div className="flex align-items-center overflow-hidden">
                                                <i className="pi pi-file-pdf text-red-500 mr-3 text-xl"></i>
                                                <div className="flex flex-column">
                                                    <span className="font-medium text-900 text-sm" title={doc.fileName}>{doc.fileName}</span>
                                                    {doc.createdDate && (
                                                        <span className="text-xs text-500">
                                                            {new Date(doc.createdDate).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                icon="pi pi-download"
                                                className="p-button-rounded p-button-text p-button-secondary"
                                                onClick={() => handleDownloadDocument(doc)}
                                                tooltip={th('common.download')}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center p-4 surface-50 border-round text-500">
                                    <i className="pi pi-folder-open text-4xl mb-2"></i>
                                    <div className="font-medium">{th('common.noDocuments')}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SAĞ TARAF: Timeline */}
                    <div className="col-12 md:col-7">
                        <div className="surface-card p-3 shadow-2 border-round h-full">
                            <div className="text-xl font-bold mb-3 text-primary border-bottom-1 surface-border pb-2">
                                {th('common.history')}
                            </div>
                            <Timeline
                                value={events}
                                marker={customizedMarker}
                                content={customizedContent}
                                className="customized-timeline"
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </Dialog>
    );
};

export default DealerInvoiceHistoryDialog;
